import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { pathFor, type Lang } from '../../../router/paths';

const ambienteImages: { src: string; alt: string }[] = [
  { src: '/images/ambiente/Kachelofenstube_Sitzecke_mit_Bibliothek_2025.jpg', alt: 'Kachelofenstube mit Bibliothek im Hotel Alpenhof Gauting' },
  { src: '/images/ambiente/Kachelofenstube_Eckbank_2025.jpg',                  alt: 'Eckbank in der Kachelofenstube – Hotel Alpenhof Gauting' },
  { src: '/images/ambiente/Fruehstuecksraum_2025.jpg',                          alt: 'Frühstücksraum mit Tageslicht – Hotel Alpenhof Gauting' },
  { src: '/images/ambiente/Fruehstueck_Buffetraum_2025.jpg',                    alt: 'Frühstücksbuffet im Hotel Alpenhof Gauting nahe Starnberger See' },
  { src: '/images/ambiente/Hotel_Sitzecke_Gaesteeingang.jpg',                   alt: 'Sitzecke am Gästeeingang – Hotel Alpenhof Gauting' },
  { src: '/images/ambiente/Hotel_alte_Villa.jpg',                               alt: 'Historische Villa des Hotel Alpenhof Gauting (Baujahr 1911)' },
  { src: '/images/ambiente/Aussen_Frontansicht_blaue_Stunde.jpg',               alt: 'Hotel Alpenhof Gauting bei Nacht – Außenansicht zur blauen Stunde' },
  { src: '/images/ambiente/Aussen_Frontansicht_Fruehling.jpg',                  alt: 'Hotel Alpenhof Gauting im Frühling – Außenansicht mit blühendem Garten' },
  { src: '/images/ambiente/Hotel_Garten_Gaesteeingang.jpg',                     alt: 'Garten am Gästeeingang – Hotel Alpenhof Gauting im Würmtal' },
  { src: '/images/ambiente/Hotel_Gartenblick_Blumentopf.jpg',                   alt: 'Gartenblick mit Blumentopf – Hotel Alpenhof Gauting' },
  { src: '/images/ambiente/Aussenansicht_Logo_Steingarten_Fruehling.jpg',       alt: 'Hotel Alpenhof Gauting – Außenansicht mit Steingarten im Frühling' },
];

export default function Tradition() {
  const { t, i18n } = useTranslation();
  const lang: Lang = i18n.language === 'en' ? 'en' : 'de';
  const [activeImg, setActiveImg] = useState<string | null>(null);

  return (
    <section className="py-12 sm:py-20 px-4 sm:px-6 bg-gradient-to-br from-amber-50 to-stone-100">
      <div className="max-w-5xl mx-auto text-center">
        <div className="mb-6 sm:mb-10">
          <p className="text-[10px] sm:text-xs font-semibold tracking-wider sm:tracking-widest uppercase text-amber-700 mb-5 sm:mb-6">{t('tradKicker')}</p>
          <div className="flex justify-center mb-6">
            {/* MOBILE: max-w-[160px] statt fixe 200px – verhindert Overflow auf sehr kleinen Screens */}
            <img
              src="/images/brand/logo_stick.webp"
              alt="Hotel Alpenhof Gauting Logo"
              className="w-full max-w-[160px] sm:max-w-[200px]"
              style={{ filter: 'drop-shadow(0 4px 18px rgba(120,60,0,0.22)) drop-shadow(0 2px 6px rgba(120,60,0,0.13))' }}
            />
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-serif text-gray-800 mb-3 sm:mb-4">
            {t('tradTitle')}
          </h2>
          <p className="text-base sm:text-lg text-gray-600 leading-relaxed max-w-2xl mx-auto">
            {t('tradLead')}
          </p>
        </div>

        <div className="mb-10 sm:mb-16">
          <Link
            to={pathFor('hotel', lang)}
            onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })}
            className="inline-block border border-amber-700 text-amber-700 px-8 py-3 rounded-md text-sm font-medium hover:bg-amber-100 transition-colors whitespace-nowrap cursor-pointer"
          >
            {t('tradCtaButton')}
          </Link>
        </div>

        {/* Ambiente Galerie */}
        <div className="text-center mb-6 sm:mb-8">
          <h3 className="text-xl sm:text-2xl font-serif text-gray-700 mb-2">{t('tradGalleryTitle')}</h3>
          <p className="text-gray-500 text-sm">{t('tradGalleryLead')}</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 sm:gap-3">
          {ambienteImages.map((image, i) => (
            <div
              key={i}
              className={`overflow-hidden rounded-lg cursor-pointer group relative ${
                i === 0 ? 'col-span-2 md:col-span-2 row-span-2' :
                i === 5 ? 'col-span-2 md:col-span-1' : ''
              }`}
              onClick={() => setActiveImg(image.src)}
            >
              <img
                src={image.src}
                alt={image.alt}
                loading="lazy"
                className={`w-full object-cover group-hover:scale-105 transition-transform duration-500 ${
                  i === 0 ? 'h-64 sm:h-80 md:h-[420px]' : 'h-40 sm:h-52 md:h-52'
                }`}
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300" />
            </div>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {activeImg && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setActiveImg(null)}
        >
          <button
            className="absolute top-5 right-6 text-white text-4xl cursor-pointer hover:text-amber-300 transition-colors"
            onClick={() => setActiveImg(null)}
          >
            <i className="ri-close-line"></i>
          </button>
          <img
            src={activeImg}
            alt="Hotel Alpenhof Gauting – Detailansicht"
            className="max-w-4xl max-h-[90vh] w-full object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </section>
  );
}
